import React, { useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import LoadingSpinner from 'components/LoadingSpinner'
import FormField from './FormField'
import FormSelect from './FormSelect'
import { connectedApiService } from '..'
import * as Yup from 'yup'
import { showNotification } from 'actions'
import FormInputDatepicker from './FormInputDatepicker'
import DynamicDropdown from './DynamicDropdown'

const AddTaskModal = ({
  isOpen,
  onClose,
  chatUser = null,
  fromGrid = false,
  hasVipMg = false,
  bookingType = 'airport_transfer',
  setEscalationService,
  escalationService,
  newTaskHandler,
  bookingId = null,
  hideRadio = false,
  showFieldsOf = null,
  showNotification,
}) => {
  const [isLoading, setLoading] = useState(false)
  const [isCustomQuote, setIsCustomQuote] = useState(false)

  if (!isOpen) return null

  const validationSchema = Yup.object().shape({
    category: Yup.string(),
    financeBookingNumber:
      bookingId || isCustomQuote
        ? Yup.string()
        : Yup.string().when('category', {
            is: 'finance',
            then: schema =>
              schema.required('Booking or quote number is required'),
            otherwise: schema => schema.notRequired(),
          }),
    bookingNumber:
      bookingId || isCustomQuote
        ? Yup.string()
        : Yup.string().when('category', {
            is: val => val !== 'finance',
            then: schema => schema.required('Booking number is required'),
            otherwise: schema => schema.notRequired(),
          }),
    link: Yup.string().matches(
      /^https?:\/\//i,
      'Must be a valid link with http:// or https://'
    ),
    financeChannelLink: Yup.string().matches(
      /^https?:\/\//i,
      'Must be a valid link with http:// or https://'
    ),
    financeChannel: Yup.object().when('category', {
      is: 'finance',
      then: schema => schema.required('Choose a channel'),
      otherwise: schema => schema.notRequired(),
    }),
    quoteNumber: isCustomQuote ? Yup.string().required() : Yup.string(),
    pickUpDate: isCustomQuote ? Yup.string().required() : Yup.string(),
    service: isCustomQuote ? Yup.string().required() : Yup.string(),
    company: isCustomQuote ? Yup.string().required() : Yup.string(),
    user: isCustomQuote ? Yup.string().required() : Yup.string(),
  })

  const initialValues = {
    category: showFieldsOf ? showFieldsOf : 'clients',
    bookingType: 'booking',
    type: 'booking',
    financeBookingNumber: '',
    financeChannel: '',
    financeChannelLink: '',
    financeRequest: '',
    bookingNumber: '',
    priority: '',
    link: '',
    team: '',
    actionNeeded: '',
    request: '',
    ...(isCustomQuote
      ? {
          quoteNumber: '',
          pickUpDate: '',
          company: chatUser ? chatUser.companyName : '',
          service: { value: 'Transfer', label: 'Transfer' },
          partner: '',
          vehicle: '',
          user: chatUser ? chatUser.firstName + ' ' + chatUser.lastName : '',
        }
      : {}),
  }

  let bookingTypes = {
    airport_transfer: 'Transfer',
    car_rentals: 'Rental',
    chauffeur_services: 'By the hour',
    departure_transfer: 'Transfer',
    return_transfer: 'Transfer',
  }

  const priorityOptions = [
    { value: 'Urgent', label: 'Urgent' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
  ]
  const teamOptions = [
    { value: 'Client Services', label: 'Client Services' },
    // { value: 'Service Delivery', label: 'Service Delivery' },
    // { value: 'Supply', label: 'Supply' },
    { value: 'Operations', label: 'Operations' },
  ]
  const channelOptions = [
    { value: 'Live Chat', label: 'Live Chat' },
    { value: 'Email', label: 'Email' },
    { value: 'WhatsApp', label: 'WhatsApp' },
  ]
  const types = [
    { value: 'Transfer', label: 'Transfer' },
    { value: 'Rental', label: 'Rental' },
    { value: 'By the hour', label: 'By the hour' },
  ]
  const escalationCategoryOptions = [
    {
      value: 'Additional Charge',
      label: 'Additional Charge',
      type: 'escalation',
      items: [
        'Client disputes waiting time charges, stating the passenger was picked up on time and no delays occurred.',
        'Additional stop fee was added, but client claims no extra stops were made.',
      ],
    },
    {
      value: 'Booking Issues',
      label: 'Booking Issues',
      type: 'escalation',
      items: [
        'Booking was mistakenly cancelled without client’s request.',
        'Date/time of booking was incorrect due to backend system glitch.',
        'Special requests (e.g., meet-and-greet sign) were not recorded in the booking notes.',
      ],
    },
    {
      value: 'Chauffeur Assignment',
      label: 'Chauffeur Assignment',
      type: 'escalation',
      items: [
        'The chauffeur assignment was made late.',
        'A different chauffeur arrived instead of the one who was assigned.',
      ],
    },
    {
      value: 'Chauffeur Behaviour',
      label: 'Chauffeur Behaviour',
      type: 'escalation',
      items: [
        'Chauffeur was rude and dismissive when the client asked for air conditioning adjustment.',
        'Chauffeur was on a personal phone call throughout the journey, making the client uncomfortable.',
        'Client reported that the chauffeur appeared distracted and drove recklessly.',
      ],
    },
    {
      value: 'Chauffeur Delayed',
      label: 'Chauffeur Delayed',
      type: 'escalation',
      items: [
        'Chauffeur arrived 25 minutes after the scheduled pick-up time without prior notice.',
        'Traffic delays caused the chauffeur to reach the airport after the client had exited the terminal.',
        'Chauffeur took a longer route causing a significant delay in arrival time.',
      ],
    },
    {
      value: 'Chauffeur No-Show',
      label: 'Chauffeur No-Show',
      type: 'escalation',
      items: [
        'Chauffeur did not arrive at the pick-up location and was uncontactable.',
        'Passengers waited at the terminal for over 30 minutes but no chauffeur turned up or called.',
        'Booking was confirmed, but no chauffeur was assigned or dispatched.',
      ],
    },
    {
      value: 'Greeter No-show',
      label: 'Greeter No-show',
      type: 'escalation',
      items: [
        'Greeter did not arrive at the pick-up location and was uncontactable.',
        'Passengers waited for over 30 minutes but no greeter turned up.',
        'Booking was confirmed, but no greeter was assigned or dispatched.',
      ],
    },
    {
      value: 'Itinerary Issues',
      label: 'Itinerary Issues',
      type: 'escalation',
      items: [
        'Chauffeur was unaware of the multiple stops listed in the itinerary.',
        'Client had an amended drop-off location that was not communicated to the chauffeur.',
        'Scheduled pickup time was incorrect due to time zone misalignment.',
      ],
    },
    {
      value: 'Payment Issues',
      label: 'Payment Issues',
      type: 'escalation',
      items: [
        'Client was charged twice for the same ride.',
        'Pre-paid booking flagged as unpaid by chauffeur upon pick-up.',
        'Incorrect billing currency applied to the final invoice.',
      ],
    },
    {
      value: 'Technical Issues',
      label: 'Technical Issues',
      type: 'escalation',
      items: [
        'Booking system did not send confirmation email to client.',
        'GPS malfunction caused chauffeur to reach a wrong location.',
        'App showed incorrect chauffeur details to the client.',
      ],
    },
    {
      value: 'Vehicle Assigned',
      label: 'Vehicle Assigned',
      type: 'escalation',
      items: [
        'A standard sedan was sent instead of the pre-booked luxury SUV.',
        'Vehicle did not have required child seat despite prior request.',
        'Chauffeur arrived in a different vehicle than what was confirmed to the client.',
      ],
    },
    {
      value: 'Vehicle Conditions',
      label: 'Vehicle Conditions',
      type: 'escalation',
      items: [
        'Vehicle was not clean and had an unpleasant odor.',
        'Air conditioning was not working during a hot afternoon ride.',
        'Car exterior had visible dents and damage, causing client concern.',
      ],
    },
  ]

  const onSubmit = async values => {
    setLoading(true)
    let payload = {}

    if (showFieldsOf === 'finance' || values.category === 'finance') {
      payload = {
        category: 'finance',
        bookingNumber: values.financeBookingNumber.trim(),
        channel: values.financeChannel.value,
        financeLink: '',
        channelLink: values.financeChannelLink,
        request: values.financeRequest,
      }
    }

    if (
      values.category === 'supply' ||
      values.category === 'clients' ||
      values.category === 'operations' ||
      showFieldsOf === 'clients' ||
      showFieldsOf === 'operations' ||
      showFieldsOf === 'supply'
    ) {
      payload = {
        category: values.category,
        bookingNumber: values.bookingNumber.trim(),
        link: values.link,
        priority: values.priority.value,
        team: values.team.value,
        actionNeeded: values.actionNeeded,
        request: values.request,
      }
    }

    if (showFieldsOf === 'escalation') {
      payload = {
        category: 'escalation',
        escalationCategory: values.escalationCategory.value,
        bookingNumber: values.bookingNumber.trim(),
        channel: values.channel.value,
        escalationLink: '',
        channelLink: values.channelLink,
        request: values.comments,
      }
    }

    if (bookingId) {
      payload.bookingId = bookingId
      delete payload.bookingNumber
    }

    if (fromGrid) {
      payload.fromGrid = true
    }
    payload.bookingType = values.bookingType

    if (isCustomQuote) {
      payload.quoteNumber = values.quoteNumber
      payload.pickUpDate = values.pickUpDate
      payload.company = values.company
      payload.user = values.user
      payload.vehicle = values.vehicle
      payload.partner = values.partner
      payload.service = values.service.label

      delete payload.bookingNumber
      delete payload.bookingType
      let result = await connectedApiService.post('tasks/customQuote', payload)
      showNotification({ message: 'Task added successfully' })
      if (fromGrid) {
        newTaskHandler(result.data)
      }
      setLoading(false)
      setIsCustomQuote(false)
      onClose()
      return
    }

    let result = await connectedApiService.post(`tasks`, payload)

    if (result.data.message === 'noBookingFound') {
      showNotification({
        message: 'Booking is not found.',
        type: 'error',
      })
      setLoading(false)
      return
    }

    if (result.data.message === 'escalationAlreadyExists') {
      showNotification({
        message: 'Escalation for this booking already exists.',
        type: 'error',
      })
      setLoading(false)
      return
    }

    showNotification({
      message: 'Task added successfully',
    })

    if (fromGrid) {
      newTaskHandler(result.data)
    }

    if (showFieldsOf === 'escalation') {
      await connectedApiService.post(
        `booking/escalation/${
          fromGrid ? result.data.bookingId : result.data.message
        }`,
        { escalationStatus: true }
      )
      if (!fromGrid) {
        setEscalationService(!escalationService)
      }
    }

    setLoading(false)
    setIsCustomQuote(false)
    onClose()
    return
  }

  return (
    <div className='task-modal-overlay'>
      {isLoading && <LoadingSpinner />}
      <div
        className='task-modal-content'
        style={
          bookingId
            ? { height: '73vh', width: '40vw' }
            : { height: '80vh', width: '35vw' }
        }
      >
        <h3 className='mb-4' style={{ fontWeight: 600 }}>
          {showFieldsOf === 'escalation' ? 'Add escalation' : 'Add a task'}
        </h3>
        <button
          className='close-task-button'
          onClick={onClose}
          disabled={isLoading}
        >
          ×
        </button>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validationContext={{ bookingId: bookingId }}
          onSubmit={onSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => {
            return (
              <Form>
                {hideRadio === false ? (
                  <div className='mb-3 d-flex task-radio-gap'>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='category'
                        value='clients'
                        checked={values.category === 'clients'}
                      />
                      Client
                    </label>
                    {/* <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='category'
                        value='supply'
                        checked={values.category === 'supply'}
                      />
                      Supply
                    </label> */}
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='category'
                        value='operations'
                        checked={values.category === 'operations'}
                      />
                      Operations
                    </label>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='category'
                        value='finance'
                        checked={values.category === 'finance'}
                      />
                      Finance
                    </label>
                  </div>
                ) : (
                  <></>
                )}
                {bookingId === null && showFieldsOf != 'escalation' ? (
                  <div className='mb-3 d-flex task-radio-gap'>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='type'
                        value='booking'
                        checked={isCustomQuote === false}
                        onChange={e => {
                          setIsCustomQuote(false)
                        }}
                      />
                      {'Platform booking/quote'}
                    </label>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='type'
                        value='customQuote'
                        checked={isCustomQuote === true}
                        onChange={e => {
                          setIsCustomQuote(true)
                        }}
                      />
                      Custom quote
                    </label>
                  </div>
                ) : (
                  <></>
                )}
                {isCustomQuote === false &&
                (hasVipMg === true || bookingId === null) ? (
                  <div className='mb-3 d-flex task-radio-gap'>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='bookingType'
                        value='booking'
                        checked={values.bookingType === 'booking'}
                      />
                      {bookingId ? bookingTypes[bookingType] : 'Service'}
                    </label>
                    <label className='task-radio-label'>
                      <Field
                        type='radio'
                        name='bookingType'
                        value='vip'
                        checked={values.bookingType === 'vip'}
                      />
                      VIP Meet & Greet
                    </label>
                  </div>
                ) : (
                  <></>
                )}
                {(values.category === 'finance' ||
                  showFieldsOf === 'finance') && (
                  <div>
                    {!bookingId && isCustomQuote === false && (
                      <div className='mb-3'>
                        <Field
                          name='financeBookingNumber'
                          label='Booking or quote number'
                          component={FormField}
                        />
                      </div>
                    )}
                    {isCustomQuote === true && (
                      <>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='quoteNumber'
                              label='Quote Number*'
                              component={FormField}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='pickUpDate'
                              label='Service Date*'
                              component={FormInputDatepicker}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='company'
                              label='Company*'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('company', value)
                              }
                              showLabel={true}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='user'
                              label='User*'
                              component={FormField}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='service'
                              label='Service*'
                              component={FormSelect}
                              options={types}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='partner'
                              label='Partner'
                              type='supplier'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('partner', value)
                              }
                              inputFor='partner'
                              showLabel={true}
                            />
                          </div>
                        </div>
                        <div className='mb-3'>
                          <Field
                            name='vehicle'
                            label='Vehicle'
                            component={FormField}
                          />
                        </div>
                      </>
                    )}
                    <div className='mb-3'>
                      <Field
                        name='financeChannel'
                        label='Channel'
                        component={FormSelect}
                        options={channelOptions}
                      />
                    </div>
                    <div className='mb-3'>
                      <Field
                        name='financeChannelLink'
                        label='Channel link'
                        component={FormField}
                      />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='financeRequest' className='form-label'>
                        Request
                      </label>
                      <Field
                        component='textarea'
                        name='financeRequest'
                        id='financeRequest'
                        className='form-control'
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {(values.category === 'supply' ||
                  values.category === 'operations' ||
                  values.category === 'clients' ||
                  showFieldsOf === 'clients' ||
                  showFieldsOf === 'operations' ||
                  showFieldsOf === 'supply') && (
                  <div>
                    {!bookingId && isCustomQuote === false && (
                      <div className='mb-3'>
                        <Field
                          name='bookingNumber'
                          label='Booking or quote number'
                          component={FormField}
                        />
                      </div>
                    )}
                    {isCustomQuote === true && (
                      <>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='quoteNumber'
                              label='Quote Number*'
                              component={FormField}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='pickUpDate'
                              label='Service Date*'
                              component={FormInputDatepicker}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='company'
                              label='Company*'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('company', value)
                              }
                              showLabel={true}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='user'
                              label='User*'
                              component={FormField}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='service'
                              label='Service*'
                              component={FormSelect}
                              options={types}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='partner'
                              label='Partner'
                              type='supplier'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('partner', value)
                              }
                              inputFor='partner'
                              showLabel={true}
                            />
                          </div>
                        </div>
                        <div className='mb-3'>
                          <Field
                            name='vehicle'
                            label='Vehicle'
                            component={FormField}
                          />
                        </div>
                      </>
                    )}
                    <div className='row mb-3'>
                      <div className='col-6'>
                        <Field
                          name='priority'
                          label='Priority'
                          component={FormSelect}
                          options={priorityOptions}
                        />
                      </div>
                      <div className='col-6'>
                        <Field
                          name='team'
                          label='Team'
                          component={FormSelect}
                          options={teamOptions}
                        />
                      </div>
                    </div>
                    <div className='mb-3'>
                      <Field
                        name='link'
                        label='slack link'
                        component={FormField}
                      />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='actionNeeded' className='form-label'>
                        Action Needed
                      </label>
                      <Field
                        component='textarea'
                        name='actionNeeded'
                        id='actionNeeded'
                        className='form-control'
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {showFieldsOf === 'escalation' && (
                  <div>
                    {!bookingId && isCustomQuote === false && (
                      <div className='mb-3'>
                        <Field
                          name='bookingNumber'
                          label='Booking'
                          component={FormField}
                        />
                      </div>
                    )}
                    {isCustomQuote === true && (
                      <>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='quoteNumber'
                              label='Quote Number*'
                              component={FormField}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='pickUpDate'
                              label='Service Date*'
                              component={FormInputDatepicker}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='company'
                              label='Company*'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('company', value)
                              }
                              showLabel={true}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='user'
                              label='User*'
                              component={FormField}
                              disabled={chatUser ? true : false}
                            />
                          </div>
                        </div>
                        <div className='row mb-1'>
                          <div className='col-6'>
                            <Field
                              name='service'
                              label='Service*'
                              component={FormSelect}
                              options={types}
                            />
                          </div>
                          <div className='col-6'>
                            <Field
                              name='partner'
                              label='Partner'
                              type='supplier'
                              component={DynamicDropdown}
                              onChange={value =>
                                setFieldValue('partner', value)
                              }
                              inputFor='partner'
                              showLabel={true}
                            />
                          </div>
                        </div>
                        <div className='mb-3'>
                          <Field
                            name='vehicle'
                            label='Vehicle'
                            component={FormField}
                          />
                        </div>
                      </>
                    )}
                    <div className='mb-3'>
                      <Field
                        name='escalationCategory'
                        label='Escalation Category'
                        component={FormSelect}
                        options={escalationCategoryOptions}
                      />
                    </div>
                    <div className='mb-3'>
                      <Field
                        name='channel'
                        label='Channel'
                        component={FormSelect}
                        options={channelOptions}
                      />
                    </div>
                    <div className='mb-3'>
                      <Field
                        name='channelLink'
                        label='Channel Link'
                        component={FormField}
                      />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='comments' className='form-label'>
                        comments
                      </label>
                      <Field
                        component='textarea'
                        name='comments'
                        id='comments'
                        className='form-control'
                        rows={3}
                      />
                    </div>
                  </div>
                )}
                <div className='task-modal-actions'>
                  <button
                    onClick={() => {
                      setIsCustomQuote(false)
                      onClose()
                    }}
                    className='btn cancel-dispatch-btn'
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isLoading}
                    type='submit'
                    style={{ padding: '0px 15px 0px 15px', height: '35px' }}
                    className='btn btn-brown'
                  >
                    Save
                  </button>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default withRouter(connect(null, { showNotification })(AddTaskModal))
